import { useState, useEffect } from "react";
import s from "./HomePage.module.css";

/* ── Floating Swedish word data ── */
const FLOAT_WORDS = [
  { sv: "Hej!", en: "Hello!", x: 8, y: 18, delay: 0, size: "lg" },
  { sv: "Tack", en: "Thanks", x: 78, y: 12, delay: 0.8, size: "md" },
  { sv: "Fika", en: "Coffee break", x: 88, y: 42, delay: 1.6, size: "sm" },
  { sv: "Lagom", en: "Just right", x: 5, y: 55, delay: 2.2, size: "md" },
  { sv: "Mysig", en: "Cozy", x: 82, y: 68, delay: 0.4, size: "sm" },
  { sv: "Lycka", en: "Happiness", x: 15, y: 78, delay: 1.2, size: "sm" },
];

/* ── Quest path step data ── */
const QUEST_STEPS = [
  {
    icon: "book",
    title: "Read a Story",
    desc: "AI crafts a Swedish story at your level. Tap any word to see its meaning instantly.",
    color: "#c08b2c",
    xp: "+15 XP",
  },
  {
    icon: "cards",
    title: "Learn the Words",
    desc: "Flip through interactive flashcards with audio pronunciation and spaced repetition.",
    color: "#2d8a5e",
    xp: "+20 XP",
  },
  {
    icon: "headphones",
    title: "Listen & Respond",
    desc: "Hear Swedish sentences and test your comprehension with guided quizzes.",
    color: "#6b7b8d",
    xp: "+25 XP",
  },
  {
    icon: "repeat",
    title: "Flashcard Review",
    desc: "Master vocabulary with smart repetition. Auto-play, customize speed, and track progress.",
    color: "#b8783e",
    xp: "+20 XP",
  },
  {
    icon: "ear",
    title: "Swedish Only Mode",
    desc: "Full immersion. Listen and respond without any English translations.",
    color: "#9e4a3e",
    xp: "+30 XP",
  },
  {
    icon: "mic",
    title: "Role Play",
    desc: "Speak aloud in real Swedish conversations. AI listens, corrects, and coaches you.",
    color: "#7a6840",
    xp: "+40 XP",
  },
];

/* ── Feature data ── */
const FEATURES = [
  {
    emoji: "\uD83E\uDD16",
    title: "AI-Powered Stories",
    desc: "Every story is uniquely generated for your level. No two learners get the same tale.",
    color: "#c08b2c",
  },
  {
    emoji: "\uD83D\uDD0A",
    title: "Native Audio",
    desc: "Hear every word, sentence, and story pronounced by natural-sounding Swedish voices.",
    color: "#6b7b8d",
  },
  {
    emoji: "\uD83C\uDFAF",
    title: "Interactive Quizzes",
    desc: "Test yourself on every sentence. Multiple question types keep things fresh.",
    color: "#b8783e",
  },
  {
    emoji: "\uD83C\uDFA4",
    title: "Speech Practice",
    desc: "Use your microphone to practice speaking. Get instant feedback on pronunciation.",
    color: "#9e4a3e",
  },
  {
    emoji: "\uD83D\uDCDA",
    title: "Word Dictionary",
    desc: "Tap any word to see its meaning, part of speech, and hear it pronounced.",
    color: "#2d8a5e",
  },
  {
    emoji: "\uD83C\uDFA7",
    title: "Downloadable Lessons",
    desc: "Take your lessons offline. Download audio to practice Swedish anywhere.",
    color: "#7a6840",
  },
];

/* ── Icon components ── */
function StepIcon({ type, color }) {
  const icons = {
    book: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
        <path d="M8 7h6" /><path d="M8 11h4" />
      </svg>
    ),
    cards: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="14" height="16" rx="2" /><path d="M18 8h2a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2" />
        <path d="M6 10h6" /><path d="M6 14h4" />
      </svg>
    ),
    headphones: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
        <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
      </svg>
    ),
    repeat: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
        <path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
      </svg>
    ),
    ear: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 18.5a3.5 3.5 0 1 0 7 0c0-1.57.92-2.52 2.04-3.46" />
        <path d="M6 8.5C6 4.36 9.36 1 13.5 1S21 4.36 21 8.5c0 2.5-1.2 4.7-3 6.1" />
        <path d="M15 8.5a2.5 2.5 0 0 0-5 0v1a2 2 0 0 0 4 0" />
      </svg>
    ),
    mic: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
      </svg>
    ),
  };
  return (
    <div className={s.stepIcon} style={{ background: color, boxShadow: `0 8px 24px ${color}44` }}>
      {icons[type]}
    </div>
  );
}

/* ── Animated XP counter ── */
function XpCounter() {
  const [xp, setXp] = useState(0);
  useEffect(() => {
    const target = 2450;
    const step = Math.ceil(target / 60);
    const id = setInterval(() => {
      setXp((prev) => {
        if (prev + step >= target) { clearInterval(id); return target; }
        return prev + step;
      });
    }, 30);
    return () => clearInterval(id);
  }, []);
  return <span>{xp.toLocaleString()}</span>;
}

export default function HomePage({ onSignup, onLogin }) {
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActiveFeature((p) => (p + 1) % FEATURES.length), 3000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className={s.page}>
      {/* ── Floating word bubbles ── */}
      <div className={s.floatingWords} aria-hidden="true">
        {FLOAT_WORDS.map((w, i) => (
          <div
            key={i}
            className={`${s.floatBubble} ${s[`float_${w.size}`]}`}
            style={{ left: `${w.x}%`, top: `${w.y}%`, animationDelay: `${w.delay}s` }}
          >
            <span className={s.floatSv}>{w.sv}</span>
            <span className={s.floatEn}>{w.en}</span>
          </div>
        ))}
      </div>

      {/* ── Navbar ── */}
      <header className={s.navbar}>
        <div className={s.navLeft}>
          <span className={s.navLogo}>Tala Svenska</span>
        </div>
        <div className={s.navRight}>
          <button className={s.signInBtn} onClick={onLogin}>Log in</button>
          <button className={s.getStartedBtn} onClick={onSignup}>Get Started</button>
        </div>
      </header>

      {/* ── Hero ── */}
      <main className={s.hero}>
        <div className={s.heroContent}>
          <div className={s.heroBadge}>
            <span className={s.badgeDot} />
            Free to start &middot; No credit card needed
          </div>

          <h1 className={s.headline}>
            Learn Swedish
            <br />
            <span className={s.headlineAccent}>the fun way.</span>
          </h1>

          <p className={s.subtitle}>
            AI-powered stories, interactive quizzes, speech practice,
            and a gamified quest path that makes learning Swedish
            feel like an adventure.
          </p>

          <div className={s.heroCtas}>
            <button className={s.ctaPrimary} onClick={onSignup}>
              Start Learning for Free
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
              </svg>
            </button>
            <div className={s.ctaMeta}>
              <div className={s.ctaAvatars}>
                <span style={{ background: "#e8c060" }}>E</span>
                <span style={{ background: "#a7c4a0" }}>K</span>
                <span style={{ background: "#c8b4e0" }}>A</span>
                <span style={{ background: "#e0b4a0" }}>M</span>
                <span style={{ background: "#d4a843" }}>S</span>
              </div>
              <span className={s.ctaCount}>Join learners mastering Swedish daily</span>
            </div>
          </div>
        </div>

        {/* ── Hero visual: Gamification showcase ── */}
        <div className={s.heroVisual}>
          <div className={s.gameCard}>
            {/* XP Bar */}
            <div className={s.xpSection}>
              <div className={s.xpHeader}>
                <span className={s.xpLabel}>Your Progress</span>
                <span className={s.xpTotal}><XpCounter /> XP</span>
              </div>
              <div className={s.xpBarOuter}>
                <div className={s.xpBarInner} />
              </div>
              <span className={s.xpLevel}>Level 12</span>
            </div>

            {/* Stats row */}
            <div className={s.statsRow}>
              <div className={s.statItem}>
                <span className={s.statEmoji}>{"\uD83D\uDD25"}</span>
                <span className={s.statValue}>14</span>
                <span className={s.statLabel}>Day Streak</span>
              </div>
              <div className={s.statDivider} />
              <div className={s.statItem}>
                <span className={s.statEmoji}>{"\u2B50"}</span>
                <span className={s.statValue}>47</span>
                <span className={s.statLabel}>Stars Earned</span>
              </div>
              <div className={s.statDivider} />
              <div className={s.statItem}>
                <span className={s.statEmoji}>{"\uD83D\uDCDA"}</span>
                <span className={s.statValue}>23</span>
                <span className={s.statLabel}>Stories Read</span>
              </div>
            </div>

            {/* Mini quest preview */}
            <div className={s.miniQuest}>
              <div className={s.miniQuestHeader}>
                <span className={s.miniQuestTitle}>Today's Quest</span>
                <span className={s.miniQuestBadge}>4/6 Complete</span>
              </div>
              <div className={s.miniSteps}>
                <div className={`${s.miniStep} ${s.miniStepDone}`} style={{ "--step-color": "#c08b2c" }}>
                  <span className={s.miniCheck}>{"\u2713"}</span>
                </div>
                <div className={s.miniConnector} />
                <div className={`${s.miniStep} ${s.miniStepDone}`} style={{ "--step-color": "#2d8a5e" }}>
                  <span className={s.miniCheck}>{"\u2713"}</span>
                </div>
                <div className={s.miniConnector} />
                <div className={`${s.miniStep} ${s.miniStepDone}`} style={{ "--step-color": "#6b7b8d" }}>
                  <span className={s.miniCheck}>{"\u2713"}</span>
                </div>
                <div className={s.miniConnector} />
                <div className={`${s.miniStep} ${s.miniStepDone}`} style={{ "--step-color": "#b8783e" }}>
                  <span className={s.miniCheck}>{"\u2713"}</span>
                </div>
                <div className={s.miniConnector} />
                <div className={`${s.miniStep} ${s.miniStepActive}`} style={{ "--step-color": "#9e4a3e" }}>
                  <span className={s.miniPulse} />
                </div>
                <div className={s.miniConnector} />
                <div className={s.miniStep} style={{ "--step-color": "#7a6840" }}>
                  <span className={s.miniLock}>{"\uD83D\uDD12"}</span>
                </div>
              </div>
            </div>

            {/* Floating achievement toast */}
            <div className={s.achievementToast}>
              <span className={s.achievementIcon}>{"\uD83C\uDFC6"}</span>
              <div>
                <span className={s.achievementTitle}>Achievement Unlocked!</span>
                <span className={s.achievementDesc}>Complete 3 stories</span>
              </div>
              <span className={s.achievementXp}>+50 XP</span>
            </div>
          </div>
        </div>
      </main>

      {/* ── How It Works: Quest Path ── */}
      <section className={s.questSection}>
        <div className={s.sectionHeader}>
          <span className={s.sectionBadge}>How It Works</span>
          <h2 className={s.sectionTitle}>Your Quest Path</h2>
          <p className={s.sectionDesc}>
            Each story unlocks a 6-step learning adventure. Complete them all to earn maximum XP!
          </p>
        </div>

        <div className={s.questPath}>
          {QUEST_STEPS.map((step, i) => (
            <div key={i} className={s.questItem} style={{ "--delay": `${i * 0.1}s` }}>
              {i > 0 && (
                <div className={s.questConnector}>
                  <svg viewBox="0 0 24 60" fill="none">
                    <path d="M12 0 L12 60" stroke={step.color} strokeWidth="3" strokeDasharray="6 4" opacity="0.3" />
                  </svg>
                </div>
              )}
              <div className={s.questRow}>
                <StepIcon type={step.icon} color={step.color} />
                <div className={s.questInfo}>
                  <div className={s.questTitleRow}>
                    <span className={s.questStepNum}>Step {i + 1}</span>
                    <span className={s.questXp} style={{ background: `${step.color}18`, color: step.color }}>
                      {step.xp}
                    </span>
                  </div>
                  <h3 className={s.questTitle}>{step.title}</h3>
                  <p className={s.questDesc}>{step.desc}</p>
                </div>
              </div>
            </div>
          ))}
          {/* Trophy at the end */}
          <div className={s.questTrophy}>
            <div className={s.trophyCircle}>
              <span>{"\uD83C\uDFC6"}</span>
            </div>
            <span className={s.trophyLabel}>Story Complete! +150 XP</span>
          </div>
        </div>
      </section>

      {/* ── Features Grid ── */}
      <section className={s.featuresSection}>
        <div className={s.sectionHeader}>
          <span className={s.sectionBadge}>Features</span>
          <h2 className={s.sectionTitle}>Everything you need to learn Swedish</h2>
          <p className={s.sectionDesc}>
            From AI-generated stories to speech recognition, we've built the most engaging way to learn Swedish.
          </p>
        </div>

        <div className={s.featuresGrid}>
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className={`${s.featureCard} ${activeFeature === i ? s.featureActive : ""}`}
              onMouseEnter={() => setActiveFeature(i)}
              style={{ "--card-accent": f.color }}
            >
              <div className={s.featureEmoji}>{f.emoji}</div>
              <h3 className={s.featureTitle}>{f.title}</h3>
              <p className={s.featureDesc}>{f.desc}</p>
              <div className={s.featureGlow} />
            </div>
          ))}
        </div>
      </section>

      {/* ── Live Demo Preview ── */}
      <section className={s.demoSection}>
        <div className={s.demoContent}>
          <div className={s.demoText}>
            <span className={s.sectionBadge}>See It In Action</span>
            <h2 className={s.sectionTitle}>Learn by doing, not memorizing</h2>
            <p className={s.sectionDesc}>
              Tap on any word to see its meaning. Listen to pronunciation.
              Practice speaking with our AI tutor. It's like having a Swedish
              friend in your pocket.
            </p>
            <ul className={s.demoChecks}>
              <li><span className={s.checkMark}>{"\u2713"}</span>Tap any word for instant translation</li>
              <li><span className={s.checkMark}>{"\u2713"}</span>Listen at normal or slow speed</li>
              <li><span className={s.checkMark}>{"\u2713"}</span>Take quizzes on every sentence</li>
              <li><span className={s.checkMark}>{"\u2713"}</span>Download lessons as MP3s</li>
            </ul>
          </div>

          <div className={s.demoCard}>
            <div className={s.demoCardHeader}>
              <div className={s.demoHeaderDots}><span /><span /><span /></div>
              <span className={s.demoHeaderTitle}>AI Tutor</span>
              <span className={s.demoOnline}><span className={s.onlineDot}/>Online</span>
            </div>

            <div className={s.demoChat}>
              <div className={s.demoBubbleAi}>
                <div className={s.demoBubbleLabel}>AI Tutor</div>
                <p className={s.demoBubbleText}>
                  Hej! <span className={s.demoHighlight}>Hur m&aring;r</span> du idag?
                </p>
                <span className={s.demoTrans}>Hi! How are you today?</span>
                <div className={s.demoTooltip}>
                  <span className={s.tooltipW}>hur m&aring;r</span>
                  <span className={s.tooltipM}>how are (feeling)</span>
                  <span className={s.tooltipT}>verb</span>
                </div>
              </div>

              <div className={s.demoBubbleUser}>
                <div className={s.demoBubbleLabel}>You</div>
                <p className={s.demoBubbleText}>Jag m&aring;r bra, tack!</p>
                <span className={s.demoTrans}>I'm fine, thanks!</span>
                <div className={s.demoWave}>
                  {Array.from({ length: 12 }).map((_, i) => <span key={i} />)}
                </div>
              </div>

              <div className={s.demoBubbleAi}>
                <div className={s.demoBubbleLabel}>
                  AI Tutor
                  <span className={s.demoBadge}>{"\u2713"} Correct!</span>
                </div>
                <p className={s.demoBubbleText}>Utm&auml;rkt! Ska vi &ouml;va lite?</p>
                <span className={s.demoTrans}>Excellent! Shall we practice a bit?</span>
              </div>

              <div className={s.demoTyping}>
                <span /><span /><span />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className={s.finalCta}>
        <div className={s.finalInner}>
          <div className={s.finalEmojis}>
            <span className={s.finalE1}>{"\uD83C\uDDF8\uD83C\uDDEA"}</span>
            <span className={s.finalE2}>{"\uD83D\uDD25"}</span>
            <span className={s.finalE3}>{"\u2B50"}</span>
            <span className={s.finalE4}>{"\uD83C\uDFC6"}</span>
          </div>
          <h2 className={s.finalTitle}>Ready to start your Swedish adventure?</h2>
          <p className={s.finalDesc}>
            Join for free. No credit card required. Start speaking Swedish today.
          </p>
          <button className={s.ctaFinal} onClick={onSignup}>
            Begin Your Quest
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className={s.footer}>
        <div className={s.footerInner}>
          <span className={s.footerBrand}>
            <img src="/swedish_flag.svg" alt="Swedish flag" className={s.footerFlag} /> Tala Svenska
          </span>
          <p className={s.footerText}>Made with Love</p>
          <p className={s.footerText}>
            <a href="mailto:salmanandb@outlook.com" className={s.footerLink}>Contact Us</a>
            <span className={s.footerDot}>&nbsp;&middot;&nbsp;</span>
            salmanandb@outlook.com
          </p>
        </div>
      </footer>
    </div>
  );
}
